import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  fetchUserInfo,
  isUserInfoNetworkFailure,
  restoreEmployeeSession,
  switchToNonPersonal,
} from "../store/authSlice";
import {
  getAccountIsPersonal,
  readEmployeeRecord,
  type EmployeeRecord,
} from "../lib/auth";
import {
  handleAuthFailure,
  isWarmContext,
  pingSiblings,
} from "../lib/sessionRecovery";
import { readCachedUserInfo } from "../lib/userInfoCache";
import { isEdgeFallbackEnabledFromCache } from "../lib/edge/edgeConfig";

/**
 * Възстановяването на служителя на общ терминал (docs/auth-access.md §Кога
 * терминалът иска ПИН): общ терминал със запазен служителски запис в
 * localStorage, но без служител в Redux → спинър, докато токенът се
 * потвърди. Изнесено от `SharedAccountGate`, защото освен „при монтиране"
 * (refresh) възстановяването се повтаря и „при смяна на режима облак/каса"
 * (docs/edge.md §Самоличност).
 *
 * Пътят е един и същ и в двата случая: кешираната самоличност за токена
 * (`lib/userInfoCache.ts`, само при ВКЛЮЧЕН резерв — §14.6 №4 на договора)
 * хидратира Redux веднага, после
 * `fetchUserInfo({ skipAuthRedirect: true })` опреснява. Провалът се чете по
 * ВИДА си, не по текста:
 *  • мрежова грешка (без HTTP отговор — облакът го няма) → с кеш служителят
 *    ОСТАВА влязъл с кешираните роли; без кеш пада до ПИН, а токенът се
 *    запомня и при следващата смяна на режима (касата вече е достижима)
 *    възстановяването тръгва наново;
 *  • HTTP отказ → `handleAuthFailure` решава „до ПИН или пълен изход" (виж
 *    lib/sessionRecovery.ts); за source "restore" записът НЕ се трие.
 *
 * Студеният старт (нито топъл маркер, нито жив съсед) иска ПИН както досега
 * и НЕ се повтаря при смяна на режима — повтаря се само провал по мрежова
 * причина на вече разрешено възстановяване.
 *
 * Връща `restoring` — гейтът показва спинър, докато е true.
 */
export function useEmployeeSessionRestore(): boolean {
  const dispatch = useAppDispatch();
  const isPersonal = getAccountIsPersonal();
  const currentUserPersonal = useAppSelector((s) => s.auth.personal);
  const edgeMode = useAppSelector((s) => s.edge.mode);

  const [restoring, setRestoring] = useState(
    () =>
      isPersonal === false &&
      currentUserPersonal !== true &&
      readEmployeeRecord() !== null,
  );
  const personalRef = useRef(currentUserPersonal);
  personalRef.current = currentUserPersonal;
  // Винаги актуалният режим — заявка, тръгнала към облака, може да се
  // провали, след като терминалът вече е минал към касата.
  const edgeModeRef = useRef(edgeMode);
  edgeModeRef.current = edgeMode;
  // Служителят, чието възстановяване падна по МРЕЖОВА причина без кеширана
  // самоличност — при смяна на режима се опитва отново.
  const networkFailedTokenRef = useRef<string | null>(null);
  // Номер на опита: при смяна на режима може да тръгне НОВ опит, докато
  // старият още чака отговор (заявка без интернет виси до таймаута на
  // браузъра). Изостаналият опит няма право да пише — иначе неговият
  // `switchToNonPersonal()` би свалил до ПИН служител, когото новият опит
  // току-що е възстановил.
  const restoreSerialRef = useRef(0);

  const restore = useCallback(
    async (rec: EmployeeRecord) => {
      const serial = (restoreSerialRef.current += 1);
      const isStale = () => serial !== restoreSerialRef.current;
      // При изключен резерв кешът не се ползва: поведението при refresh
      // остава точно каквото беше преди офлайн режима (§14.6 №4).
      const cachedUserInfo = isEdgeFallbackEnabledFromCache()
        ? readCachedUserInfo(rec.t)
        : null;
      dispatch(restoreEmployeeSession({ token: rec.t, cachedUserInfo }));
      const refresh = (async () => {
        // Цикълът се върти САМО когато режимът се е сменил, докато заявката
        // е летяла (студен старт: облакът отказва, касата тъкмо е поела) —
        // иначе провалът на едната страна би останал последна дума.
        for (;;) {
          const modeAtStart = edgeModeRef.current;
          try {
            await dispatch(fetchUserInfo({ skipAuthRedirect: true })).unwrap();
            if (isStale()) return;
            networkFailedTokenRef.current = null;
            return;
          } catch (error) {
            if (isStale()) return;
            if (isUserInfoNetworkFailure(error)) {
              networkFailedTokenRef.current = rec.t;
              // С кеш: ролите вече са в Redux (restoreEmployeeSession),
              // нищо не се сваля. Без кеш: нов опит, ако режимът вече е
              // друг; иначе до ПИН, но записът остава — следващият опит е
              // при смяна на режима, а съседен прозорец може да работи с
              // него.
              if (cachedUserInfo) return;
              if (edgeModeRef.current !== modeAtStart) continue;
              dispatch(switchToNonPersonal());
              return;
            }
            // Отказ от сървъра: sessionRecovery решава. Без кеш решението
            // се изчаква ПРЕДИ спинърът да падне, иначе ПИН екранът се
            // появява и до 5 с по-късно се презарежда под ръцете на
            // служителя. Чистим само Redux, за да тръгне екранът с
            // терминалния токен X.
            await handleAuthFailure({ failedToken: rec.t, source: "restore" });
            dispatch(switchToNonPersonal());
            return;
          }
        }
      })();
      // С кеширана самоличност служителят е влязъл ВЕДНАГА, а живият
      // /user/info опреснява на заден план (без интернет би висял до
      // таймаута на браузъра — не и спинърът). Без кеш чакаме отговора.
      if (!cachedUserInfo) await refresh;
    },
    [dispatch],
  );

  // При монтиране (refresh): студеният старт (изключена и пусната наново
  // каса) иска ПИН; топъл контекст е refresh на вече работил прозорец; нов
  // прозорец до жив съсед се разпознава по отговора на ping-а.
  useEffect(() => {
    const warm = isWarmContext();
    if (!restoring) return;
    const rec = readEmployeeRecord();
    if (!rec) {
      setRestoring(false);
      return;
    }
    (async () => {
      try {
        if (!warm && !(await pingSiblings())) {
          setRestoring(false);
          return;
        }
        await restore(rec);
      } finally {
        setRestoring(false);
      }
    })();
    // Еднократно при монтиране — restoring само се сваля оттук.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // При смяна на режима облак/каса: провалено по мрежова причина
  // възстановяване тръгва наново срещу новия източник (в локален режим
  // касата сервира /user/info от snapshot-а).
  const modeRef = useRef(edgeMode);
  useEffect(() => {
    if (modeRef.current === edgeMode) return;
    modeRef.current = edgeMode;
    if (isPersonal !== false || personalRef.current === true) return;
    const rec = readEmployeeRecord();
    if (!rec || rec.t !== networkFailedTokenRef.current) return;
    setRestoring(true);
    void restore(rec).finally(() => setRestoring(false));
  }, [edgeMode, isPersonal, restore]);

  return restoring;
}
